import { Link } from 'react-router-dom';
import { Search, Plus, Calendar, Filter, X, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import './PageBar.css';

const DateFilter = ({ label, value, onChange }) => (
  <div className="page-bar-clean__date-filter">
    <span className="page-bar-clean__date-label">{label}</span>
    <div className="page-bar-clean__date-input-wrap">
      <Calendar size={14} className="page-bar-clean__date-icon" />
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="page-bar-clean__date-input"
      />
    </div>
  </div>
);

const SelectFilter = ({ label, value, options, onChange, placeholder = 'Все' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options?.find((opt) => String(opt.value) === String(value));

  return (
    <div className="page-bar-clean__select" ref={ref}>
      {label && <span className="page-bar-clean__select-label">{label}</span>}
      <button
        type="button"
        className="page-bar-clean__select-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <ChevronDown size={16} className={`page-bar-clean__select-chevron ${isOpen ? 'open' : ''}`} />
      </button>
      {isOpen && (
        <div className="page-bar-clean__select-dropdown">
          <button
            type="button"
            className={`page-bar-clean__select-option ${!value ? 'active' : ''}`}
            onClick={() => { onChange(null); setIsOpen(false); }}
          >
            {placeholder}
          </button>
          {options?.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`page-bar-clean__select-option ${String(value) === String(opt.value) ? 'active' : ''}`}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const PageBar = ({
  searchValue = '',
  onSearchChange,
  onSearch,
  searchPlaceholder = 'Поиск...',
  createLink,
  createLabel,
  filters,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
  children,
  rightContent,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch();
    }
  };

  const hasActiveFilters = filters?.some((f) => f.value);

  return (
    <div className="page-bar-clean">
      <div className="page-bar-clean__left">
        {onSearchChange && (
          <div className="page-bar-clean__search">
            <Search size={18} className="page-bar-clean__search-icon" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              className="page-bar-clean__search-input"
            />
            {onSearch && (
              <button onClick={onSearch} className="page-bar-clean__search-btn">
                Найти
              </button>
            )}
          </div>
        )}

        {filters && filters.length > 0 && (
          <div className="page-bar-clean__filters">
            {filters.map((filter) => {
              if (filter.type === 'date') {
                return (
                  <DateFilter
                    key={filter.key}
                    label={filter.label}
                    value={filter.value}
                    onChange={(val) => onFilterChange(filter.key, val)}
                  />
                );
              }
              if (filter.type === 'select') {
                return (
                  <SelectFilter
                    key={filter.key}
                    label={filter.label}
                    value={filter.value}
                    options={filter.options}
                    placeholder={filter.placeholder}
                    onChange={(val) => onFilterChange(filter.key, val)}
                  />
                );
              }
              return null;
            })}

            {onApplyFilters && (
              <button onClick={onApplyFilters} className="page-bar-clean__filter-apply">
                <Filter size={14} />
                Применить
              </button>
            )}

            {onResetFilters && hasActiveFilters && (
              <button onClick={onResetFilters} className="page-bar-clean__filter-reset">
                <X size={14} />
                Сбросить
              </button>
            )}
          </div>
        )}

        {children}
      </div>

      <div className="page-bar-clean__right">
        {rightContent}
        {createLink && (
          <Link to={createLink} className="page-bar-clean__create-btn">
            <Plus size={18} />
            {createLabel && <span>{createLabel}</span>}
          </Link>
        )}
      </div>
    </div>
  );
};

export default PageBar;
